<script setup>
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
  basketEndpoint: { type: String, required: true },
  saveEndpoint: { type: String, required: true },
  removeBaseUrl: { type: String, required: true },
  continueUrl: { type: String, required: true },
  confirmRemoveText: { type: String, required: true },
  totalLabel: { type: String, required: true },
  productLabel: { type: String, required: true },
  quantityLabel: { type: String, required: true },
  priceLabel: { type: String, required: true },
  sumLabel: { type: String, required: true },
  operationLabel: { type: String, required: true },
  deleteLabel: { type: String, required: true },
  continueOrderLabel: { type: String, required: true },
  currencyLabel: { type: String, default: 'грн' },
  loadingText: { type: String, default: 'Загрузка...' },
  requiredErrorText: { type: String, default: 'Поле обязательно для заполнения!' },
  invalidNumberText: { type: String, default: 'Число не может быть меньше 1!' }
});

const basket = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const hasSaveError = ref(false);

const hasValidationError = computed(() =>
  basket.value.some((product) => !isPositiveInteger(product.pcs))
);

const totalPrice = computed(() => {
  const total = basket.value.reduce((sum, product) => {
    return sum + Number(product.price || 0) * Number(product.pcs || 0);
  }, 0);

  return total.toFixed(2);
});

onMounted(() => {
  loadBasket();
});

function normalizeBasket(data) {
  if (Array.isArray(data)) {
    return data.map(normalizeProduct);
  }

  if (data && typeof data === 'object') {
    return Object.values(data).map(normalizeProduct);
  }

  return [];
}

function normalizeProduct(product) {
  return {
    ...product,
    pcs: sanitizeQuantity(product.pcs)
  };
}

function sanitizeQuantity(value) {
  const digits = String(value ?? '')
    .replace(/[^0-9]/g, '')
    .replace(/^0+(\d)/, '$1');

  return digits === '' ? '' : digits;
}

function isPositiveInteger(value) {
  return /^[1-9]\d*$/.test(String(value ?? ''));
}

async function loadBasket() {
  isLoading.value = true;

  try {
    const response = await fetch(props.basketEndpoint, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    const payload = await response.json();
    basket.value = payload.success ? normalizeBasket(payload.data) : [];
  } catch (error) {
    basket.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function saveBasket() {
  if (hasValidationError.value || isSaving.value) {
    return;
  }

  isSaving.value = true;
  hasSaveError.value = false;

  try {
    const response = await fetch(props.saveEndpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(basket.value)
    });

    const payload = await response.json();
    hasSaveError.value = !payload.success;
  } catch (error) {
    hasSaveError.value = true;
  } finally {
    isSaving.value = false;
  }
}

function onQuantityInput(product) {
  product.pcs = sanitizeQuantity(product.pcs);

  if (isPositiveInteger(product.pcs)) {
    saveBasket();
  }
}

function removeProduct(product, index) {
  if (!window.confirm(props.confirmRemoveText)) {
    return;
  }

  if (window.stats && typeof window.stats.sendRemoveProductFromBasket === 'function') {
    window.stats.sendRemoveProductFromBasket({
      name: product.good_name,
      key: product['1c_key'],
      price: product.price,
      currency: 'UAH',
      qnt: product.pcs
    });
  }

  window.setTimeout(() => {
    window.location.assign(`${props.removeBaseUrl}${index}`);
  }, 2000);
}
</script>

<template>
  <div class="vue-basket">
    <div v-if="isLoading" class="vue-basket__status">
      {{ loadingText }}
    </div>

    <template v-else>
      <form class="vue-basket__form" @submit.prevent>
        <table width="100%" cellpadding="7" cellspacing="0" align="center">
          <tr>
            <th class="cart-name">{{ productLabel }}</th>
            <th class="cart-quant">{{ quantityLabel }}</th>
            <th class="cart-price">{{ priceLabel }}</th>
            <th>{{ sumLabel }}</th>
            <th class="cart-del">{{ operationLabel }}</th>
          </tr>
          <tr v-for="(product, index) in basket" :key="product.good_id || index">
            <td class="cart-name">
              <strong>{{ product.good_name }}</strong>
            </td>
            <td class="cart-quant">
              <input
                v-model="product.pcs"
                class="input-micro"
                min="1"
                inputmode="numeric"
                @input="onQuantityInput(product)"
              >
            </td>
            <td class="cart-price">
              <span>{{ product.price }} {{ currencyLabel }}</span>
            </td>
            <td class="cart-price">
              {{ (Number(product.price || 0) * Number(product.pcs || 0)).toFixed(2) }}&nbsp;{{ currencyLabel }}
            </td>
            <td class="cart-del">
              <a :title="deleteLabel" href="#" @click.prevent="removeProduct(product, index)">
                <i class="fa fa-trash-o" aria-hidden="true"></i>&nbsp;
              </a>
            </td>
          </tr>
          <tr>
            <td colspan="5" align="right">
              <strong>{{ totalLabel }}: <span class="sum">{{ totalPrice }}</span> {{ currencyLabel }}</strong>
            </td>
          </tr>
        </table>
      </form>

      <div v-if="hasValidationError" role="alert" class="vue-basket__status vue-basket__status--error">
        <span class="error_form">{{ requiredErrorText }}</span>
        <span class="error_form">{{ invalidNumberText }}</span>
      </div>

      <div v-if="hasSaveError" role="alert" class="vue-basket__status vue-basket__status--error">
        <span class="error_form">Не удалось сохранить корзину. Попробуйте еще раз.</span>
      </div>

      <div class="center vue-basket__actions">
        <a v-if="!hasValidationError" :href="continueUrl">
          <span id="placeod" class="button green"><sub></sub><em>{{ continueOrderLabel }}</em><sup></sup></span>
        </a>
      </div>
    </template>
  </div>
</template>
